/**
 * Voluum-style group header actions: move up, move down, duplicate, delete.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { ArrowDown, ArrowUp, Copy, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/layout/Button';
import { cn } from '@/lib/utils';

export interface PathGroupHeaderActionsProps {
  groupName: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const ACTION_BUTTON_CLASS = cn(
  'h-6 w-6 shrink-0 text-muted-foreground transition-colors hover:text-foreground',
);

const PathGroupHeaderActions: FC<PathGroupHeaderActionsProps> = ({
  groupName,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
}) => {
  const stop = (cb: () => void) => (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    cb();
  };

  return (
    <div
      className="flex shrink-0 items-center gap-0.5"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        disabled={!canMoveUp}
        onClick={stop(onMoveUp)}
        aria-label={`Move ${groupName} up`}
        title="Move up"
        className={ACTION_BUTTON_CLASS}
      >
        <ArrowUp className="h-3 w-3" aria-hidden />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        disabled={!canMoveDown}
        onClick={stop(onMoveDown)}
        aria-label={`Move ${groupName} down`}
        title="Move down"
        className={ACTION_BUTTON_CLASS}
      >
        <ArrowDown className="h-3 w-3" aria-hidden />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={stop(onDuplicate)}
        aria-label={`Duplicate ${groupName}`}
        title="Duplicate"
        className={ACTION_BUTTON_CLASS}
      >
        <Copy className="h-3 w-3" aria-hidden />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={stop(onDelete)}
        aria-label={`Delete ${groupName}`}
        title="Delete"
        className="h-6 w-6 shrink-0 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
      >
        <Trash2 className="h-3 w-3" aria-hidden />
      </Button>
    </div>
  );
};

export default PathGroupHeaderActions;
